import { BLEND_MODES, Container, Filter, Matrix, Rectangle, Renderer, Sprite, filters } from "pixi.js"
import { FOCUS_ALIGN, FOCUS_PHASE, StoreyFocusFilter } from "./storey_focus_filter"

const inverseProjection = new Matrix()

/** The area the current render target shows, in the global coordinates filters work in. */
const viewArea = (renderer: Renderer, area: Rectangle): Rectangle => {
    area.copyFrom(renderer.renderTexture.sourceFrame)
    // FilterSystem expects global coordinates before the optional render projection.
    // Read the current target here so resize, nested filters and PiP renders all work.
    const projection = renderer.projection.transform
    if (projection) {
        const matrix = inverseProjection.copyFrom(projection).invert()
        const left = area.x, top = area.y, right = area.right, bottom = area.bottom
        const x1 = matrix.a * left + matrix.c * top + matrix.tx
        const x2 = matrix.a * right + matrix.c * top + matrix.tx
        const x3 = matrix.a * left + matrix.c * bottom + matrix.tx
        const x4 = matrix.a * right + matrix.c * bottom + matrix.tx
        const y1 = matrix.b * left + matrix.d * top + matrix.ty
        const y2 = matrix.b * right + matrix.d * top + matrix.ty
        const y3 = matrix.b * left + matrix.d * bottom + matrix.ty
        const y4 = matrix.b * right + matrix.d * bottom + matrix.ty
        area.x = Math.min(x1, x2, x3, x4)
        area.y = Math.min(y1, y2, y3, y4)
        area.width = Math.max(x1, x2, x3, x4) - area.x
        area.height = Math.max(y1, y2, y3, y4) - area.y
    }
    return area
}

/**
 * A filtered storey is drawn into an empty texture first, then laid over the scene. Pixi's MULTIPLY and ADD
 * add their own alpha to what they land on, so in that texture a shade with none of the storey's art under
 * it (a roof's shading spilling onto the storey below) came out opaque black, and a glow covered what it
 * should have lit. Keeping the texture's alpha leaves such a shade out and lets a glow add to the scene when
 * the texture is laid down; over the opaque screen it is what pixi draws.
 */
const LIGHTING_BLENDS = [BLEND_MODES.MULTIPLY, BLEND_MODES.ADD]

const withStoreyBlends = (renderer: Renderer, draw: () => void): void => {
    const modes = (renderer.state as unknown as { blendModes?: number[][] })?.blendModes, gl = renderer.gl
    if (!modes || !gl) { draw(); return }
    const original = LIGHTING_BLENDS.map(blend => modes[blend])
    const swap = (next: number[][]) => {
        renderer.batch.flush()
        LIGHTING_BLENDS.forEach((blend, i) => { modes[blend] = next[i] })
        // The state caches the mode by number: forget it, so the next batch applies the swap.
        renderer.state.blendMode = -1 as BLEND_MODES
    }
    swap(original.map(([source, destination]) => [source, destination, gl.ZERO, gl.ONE]))
    try {
        draw()
    } finally {
        swap(original)
    }
}

const isHeightBlur = (filter: Filter): boolean => filter instanceof filters.BlurFilter || filter instanceof StoreyFocusFilter

/**
 * The children a storey's blur leaves sharp. Looking down, the stairs and ladders (`heightBlurExempt`) stay
 * crisp: they are the way between the storeys. A storey above the player is out of focus as a whole, its
 * stairs included; only what is marked `focusBlurExempt` (the nameplates) stays sharp there.
 */
const exemptFrom = (blur: Filter) => blur instanceof StoreyFocusFilter ? blur.exempt : "heightBlurExempt"

/**
 * A storey's root. Its height blur softens the scenery; the children it exempts (exemptFrom) then draw
 * sharp over it, through the floor's other filters only.
 */
export class StoreyContainer extends Container {
    /**
     * Set by a subclass around a draw: the area its height blur covers, taken as given (StoreyFocusFilter anchors it
     * to the storey's art, and pixi would round it back onto the screen's pixels). Only the blurred pass uses it; the
     * sharp children's pass keeps `filterArea` and pixi's rounding, so they land on the screen's pixels as ever.
     */
    protected blurArea?: Rectangle

    /** The scenery's pass, over `blurArea` when there is one. */
    private renderBlurred(renderer: Renderer): void {
        const area = this.blurArea
        if (!area) { super.renderAdvanced(renderer); return }
        const previous = this.filterArea
        const system = renderer.filter as unknown as { roundFrame: (...args: unknown[]) => void }
        const round = system.roundFrame
        this.filterArea = area
        system.roundFrame = () => undefined
        try {
            super.renderAdvanced(renderer)
        } finally {
            system.roundFrame = round
            this.filterArea = previous
        }
    }

    /** Only the children `flag` exempts, through the floor's filters but its height blur: what stays sharp over it. */
    protected renderExempt(renderer: Renderer, flag: string): void {
        const sharp = this.children.filter(child => (child as Container & Record<string, unknown>)[flag] && child.renderable)
        if (!sharp.length) return
        const renderable = this.children.map(child => child.renderable), filters = this.filters
        try {
            const sharpSet = new Set(sharp)
            this.children.forEach(child => { child.renderable = sharpSet.has(child) })
            this.filters = filters?.filter(filter => !isHeightBlur(filter)) ?? null
            super.renderAdvanced(renderer)
        } finally {
            this.filters = filters
            this.children.forEach((child, index) => { child.renderable = renderable[index] })
        }
    }

    protected renderAdvanced(renderer: Renderer): void {
        const originalFilters = this.filters
        const blur = originalFilters?.find(filter => filter.enabled && isHeightBlur(filter))
        const flag = blur && exemptFrom(blur)
        const sharp = flag
            ? this.children.filter(child => (child as Container & Record<string, unknown>)[flag] && child.renderable)
            : []
        const renderable = sharp.length ? this.children.map(child => child.renderable) : []
        const draw = () => {
            // Draw the scenery through the height blur, then the exempt children through shade only.
            // Both passes retain the floor's transform, alpha and cutaway mask.
            sharp.forEach(child => { child.renderable = false })
            this.renderBlurred(renderer)
            if (sharp.length) {
                const sharpSet = new Set(sharp)
                this.children.forEach(child => { child.renderable = sharpSet.has(child) })
                this.filters = originalFilters.filter(filter => !isHeightBlur(filter))
                super.renderAdvanced(renderer)
            }
        }
        try {
            if (originalFilters?.some(filter => filter.enabled)) withStoreyBlends(renderer, draw)
            else draw()
        } finally {
            this.filters = originalFilters
            if (sharp.length) this.children.forEach((child, index) => { child.renderable = renderable[index] })
        }
    }
}

/**
 * The area a storey out of focus is blurred over, on a grid anchored to the storey's own art (`origin`, its
 * world position, off it by FOCUS_PHASE) instead of the screen's pixels: its bounds widened by `margin` (the blur's
 * reach), within the view widened by one `cell`, snapped outward to a grid of `cell` logical px. A cell past the screen's edge
 * is wider than the blur's reach at every resolution the filter works at, and stopping there keeps a
 * full-width storey's texture to the screen's own power of two (a reach past it doubled it, 32 MB at 2000px).
 * Undefined when the storey is nowhere near the view.
 */
export const anchoredFocusArea = (origin: { x: number; y: number }, bounds: Rectangle, view: Rectangle,
    margin: number, cell: number, out = new Rectangle()): Rectangle | undefined => {
    const left = Math.max(bounds.x - margin, view.x - cell), top = Math.max(bounds.y - margin, view.y - cell)
    const right = Math.min(bounds.right + margin, view.right + cell), bottom = Math.min(bounds.bottom + margin, view.bottom + cell)
    if (right <= view.x || left >= view.right || bottom <= view.y || top >= view.bottom || right <= left || bottom <= top) return undefined
    // In whole cells from the grid's origin, so the size is a whole number of cells exactly.
    const phase = cell * FOCUS_PHASE / FOCUS_ALIGN
    const index = (value: number, from: number, round: (n: number) => number) => round((value - from - phase) / cell)
    const x0 = index(left, origin.x, Math.floor), y0 = index(top, origin.y, Math.floor)
    out.x = origin.x + phase + x0 * cell
    out.y = origin.y + phase + y0 * cell
    out.width = (index(right, origin.x, Math.ceil) - x0) * cell
    out.height = (index(bottom, origin.y, Math.ceil) - y0) * cell
    return out
}

/** While something on a blurred storey moves, it is redrawn this often (ms): out of focus, 20 Hz is plenty. */
export const FOCUS_MOTION_MS = 50
/** A still storey is redrawn this often anyway (ms), for what no version tracks (the sun's shadows, animated tiles). */
export const FOCUS_SAFETY_MS = 1000
/** Stale snapshots are redrawn one storey at a time, at least this far apart (about a frame at 120Hz). */
const FOCUS_REFRESH_GAP_MS = 7
/** How far past the view a redrawn snapshot reaches, as a share of the view: the camera may travel that far on it. */
const FOCUS_SLACK = 0.25
/** The widest texture a redraw may need, in texels: slack past it would double a pooled texture again. */
const FOCUS_MAX_TEXELS = 2048
let lastStaleRefresh = -Infinity

/**
 * Whether a blurred storey's kept snapshot (`placed`: where it lands now, in global logical px) can be laid down
 * again instead of redrawing the storey: unusable unless made the same way (`key`, which carries the art's
 * version) and covering the area the view `needs`; stale once something on the storey moved after it was made
 * (`changedAt`) and FOCUS_MOTION_MS have passed, or FOCUS_SAFETY_MS in any case.
 */
export const focusSnapshotState = (placed: { x: number; y: number; width: number; height: number; key: string; madeAt: number } | undefined,
    needs: Rectangle, key: string, now: number, changedAt: number): "fresh" | "stale" | "unusable" => {
    if (!placed || placed.key !== key) return "unusable"
    const covers = placed.x <= needs.x + 1e-6 && placed.y <= needs.y + 1e-6 &&
        placed.x + placed.width >= needs.right - 1e-6 && placed.y + placed.height >= needs.bottom - 1e-6
    if (!covers) return "unusable"
    const age = now - placed.madeAt
    if (age >= FOCUS_SAFETY_MS) return "stale"
    return changedAt > placed.madeAt && age >= FOCUS_MOTION_MS ? "stale" : "fresh"
}

/**
 * An upper storey: its filters cover only its own art, and a storey wholly off screen is not drawn,
 * so a castle's far towers cost no filter passes.
 *
 * Out of focus, its blur is laid on a grid anchored to its own art. pixi rounds a filter's area to whole
 * screen pixels and clips it to the screen, so as the camera glided the storey's (nearest-sampled) art fell
 * on the blur's grid at a new offset every frame, rows 6 or 7 pixels tall in turn at 6.5 to an art pixel,
 * and the blur smeared that into a shimmer. On its own grid the art fills the same texels every frame, and
 * the blurred result lands on the screen's pixels as the sharp art does (StoreyFocusFilter).
 *
 * Being the same every frame, the blurred picture is kept (StoreyFocusFilter.snapshot) and laid down again as one
 * quad: no pass of its own, no switch of render target (on Apple GPUs each wrote out and reloaded the whole
 * screen), none of the storey's tile layers or sprites. Behind a nine-storey house the eight storeys above cost
 * about 3 ms of GPU a frame drawn live at 2582x1936, enough to drop 120 fps to 70. The storey is redrawn when the
 * snapshot was made differently (blur, alpha, cutaway, the art's version), no longer covers the view, or is stale
 * (focusSnapshotState: what moves up there updates at FOCUS_MOTION_MS), the stale ones one storey per frame.
 */
export class BoundedStoreyContainer extends StoreyContainer {
    private view = new Rectangle()
    private wide = new Rectangle()
    private bounds = new Rectangle()
    private area = new Rectangle()

    protected renderAdvanced(renderer: Renderer): void {
        if (this.filterArea || !this.filters?.some(filter => filter.enabled)) { super.renderAdvanced(renderer); return }
        // The bounds the filter would measure anyway, measured once and reused as its area.
        const bounds = this.bounds.copyFrom(this.getBounds(true))
        const view = viewArea(renderer, this.view)
        const focus = this.filters.find(filter => filter.enabled && filter instanceof StoreyFocusFilter) as StoreyFocusFilter | undefined
        if (focus) {
            const origin = { x: this.worldTransform.tx, y: this.worldTransform.ty }
            const cell = FOCUS_ALIGN / focus.resolution
            const needs = anchoredFocusArea(origin, bounds, view, focus.margin, cell, this.area)
            if (!needs) return
            const mask = (this.mask as Sprite | null)?.texture?.baseTexture as { uid?: number } | undefined
            // A shade drawn ahead of the blur is in the picture too.
            const shade = this.filters.map(filter => filter !== focus && filter.enabled && filter instanceof filters.ColorMatrixFilter
                ? filter.matrix.join(",") : "").join("")
            const key = `${focus.sigma}|${focus.resolution}|${this.worldAlpha}|${mask?.uid ?? "-"}|${focus.contentVersion}|${shade}`
            const now = performance.now(), snapshot = focus.snapshot
            const placed = snapshot && { x: snapshot.frame.x - snapshot.origin.x + origin.x, y: snapshot.frame.y - snapshot.origin.y + origin.y,
                width: snapshot.frame.width, height: snapshot.frame.height, key: snapshot.key, madeAt: snapshot.madeAt }
            const state = focusSnapshotState(placed, needs, key, now, focus.contentChangedAt)
            if (state === "fresh" || (state === "stale" && now - lastStaleRefresh < FOCUS_REFRESH_GAP_MS)) {
                renderer.batch.flush()
                focus.drawSnapshot(renderer, placed.x, placed.y, origin)
                this.renderExempt(renderer, "focusBlurExempt")
                return
            }
            if (state === "stale") lastStaleRefresh = now
            // Redrawn past the view by some slack, so the camera can move on the snapshot before it must redraw.
            const room = (size: number) => Math.min(size * FOCUS_SLACK, Math.max(0, (FOCUS_MAX_TEXELS / focus.resolution - size) / 2 - 2 * cell))
            const wide = this.wide.copyFrom(view).pad(room(view.width), room(view.height))
            const area = anchoredFocusArea(origin, bounds, wide, focus.margin, cell, this.area)
            focus.keep = { key, at: now, origin }
            focus.anchor = origin
            this.blurArea = area
            try {
                super.renderAdvanced(renderer)
            } finally {
                this.blurArea = undefined
                focus.anchor = undefined
            }
            return
        }
        if (bounds.right <= view.x || bounds.x >= view.right || bounds.bottom <= view.y || bounds.y >= view.bottom) return
        this.filterArea = bounds
        try {
            super.renderAdvanced(renderer)
        } finally {
            this.filterArea = null
        }
    }
}

/**
 * A room-wide filter needs the render view, not the bounds of every tree in the room.
 *
 * Far below the player the overworld is out of focus (StoreyFocusFilter), its blur anchored to its own art as an
 * upper storey's is (BoundedStoreyContainer): the view widened by the blur's reach, on the storey's own grid. Over
 * the screen's own area, rounded to its pixels, pixi's BlurFilter took the forest's nearest-sampled art at a new
 * offset every step of the camera, and it shimmered. It is drawn live every frame, with no kept snapshot: down
 * there the water, the grass and the crowds never stand still.
 */
export class ViewFilteredContainer extends StoreyContainer {
    private area = new Rectangle()
    private anchored = new Rectangle()

    protected renderAdvanced(renderer: Renderer): void {
        const previousArea = this.filterArea
        let focus: StoreyFocusFilter | undefined
        if (!previousArea && this.filters?.some(filter => filter.enabled)) {
            const view = this.filterArea = viewArea(renderer, this.area)
            focus = this.filters.find(filter => filter.enabled && filter instanceof StoreyFocusFilter) as StoreyFocusFilter | undefined
            if (focus) {
                focus.anchor = { x: this.worldTransform.tx, y: this.worldTransform.ty }
                this.blurArea = anchoredFocusArea(focus.anchor, view, view, focus.margin, FOCUS_ALIGN / focus.resolution, this.anchored)
            }
        }
        try {
            super.renderAdvanced(renderer)
        } finally {
            // Do not constrain bounds queries outside this draw (occlusion and hit testing).
            this.filterArea = previousArea
            this.blurArea = undefined
            if (focus) focus.anchor = undefined
        }
    }
}
